/**
 * Data layer for the inline Evidence sub-agent child run: pure, render-free
 * helpers over the child's flat `ChatItem[]` transcript — search-tool
 * classification, `ToolResult` → search-row/`Source[]` mapping, and building
 * the per-child `CitationRegistry`. Kept out of `EvidenceChildTimeline.tsx` so
 * that file only exports a component (react-refresh/only-export-components).
 */

import { readToolData } from '@/features/projects/timeline/evidence-tool-adapters/shared/readers';
import type { ChatItem, ToolUseItem } from '@heidi/agents/contracts';
import type { Source } from '@heidi/evidence/contracts';
import {
  buildCitationRegistry,
  type CitationRegistry,
  type EvidenceAgentImage,
  type EvidenceAgentSearchPayloadV1,
  parseEvidenceAgentSearchPayload,
  toChatSource,
} from './citation-tokens/citation-model';
import type { EvidenceSearchRow } from './EvidenceSearchGroup';

// Child search tool wire names. Web + partner searches render as the grouped
// `InlineSearchBlock` rollup; library search renders bare. `flag_drug_reference`
// is a silent signal.
export const WEB_STYLE_SEARCH_TOOLS = new Set([
  'web.evidence_search',
  'integrations.evidence_partner_search',
]);
export const LIBRARY_SEARCH_TOOL = 'library.search';
const EVIDENCE_SEARCH_TOOLS = new Set([...WEB_STYLE_SEARCH_TOOLS, LIBRARY_SEARCH_TOOL]);

const isEvidenceChildSearchTool = (name: string): boolean =>
  EVIDENCE_SEARCH_TOOLS.has(name);

export const toSearchStatus = (
  status: ToolUseItem['status']
): 'searching' | 'completed' =>
  // error/cancelled deliberately render as 'completed' (settled row), matching the
  // standalone surface — an errored search reads as "no sources found", not stuck.
  status === 'complete' || status === 'error' || status === 'cancelled'
    ? 'completed'
    : 'searching';

export const readQuery = (item: ToolUseItem): string =>
  typeof item.input.query === 'string' ? item.input.query : '';

export const readSources = (item: ToolUseItem): Source[] => {
  const payload = parseEvidenceAgentSearchPayload(readToolData(item));
  if (!payload) return [];
  return payload.sources
    .map(toChatSource)
    .filter((source): source is Source => source !== null);
};

export const toSearchRow = (item: ToolUseItem): EvidenceSearchRow => ({
  query: readQuery(item),
  sources: readSources(item),
  status: toSearchStatus(item.status),
});

/** Collect payloads across the child's search tool results (for the registries). */
export const collectEvidencePayloads = (items: readonly ChatItem[]) =>
  items.flatMap((item) => {
    if (item.kind !== 'tool_use' || !isEvidenceChildSearchTool(item.name)) return [];
    const payload = parseEvidenceAgentSearchPayload(readToolData(item));
    return payload ? [payload] : [];
  });

/** Build the per-child citation registry from the child's search tool payloads. */
export const buildRegistryFromItems = (items: readonly ChatItem[]): CitationRegistry =>
  buildCitationRegistry(collectEvidencePayloads(items));

/**
 * Aggregate images across the child's parsed search payloads. The BE dedups and
 * caps within a single payload, but a child makes several searches — merge
 * here and dedup by `url`, preserving first-seen order. Takes payloads (not
 * items) so callers that also build registries parse each tool result once.
 */
export const collectEvidenceImages = (
  payloads: readonly EvidenceAgentSearchPayloadV1[]
): EvidenceAgentImage[] => {
  const seen = new Set<string>();
  const images: EvidenceAgentImage[] = [];
  for (const payload of payloads) {
    for (const image of payload.images) {
      const identity = image.figure_id ?? image.url;
      if (!identity || seen.has(identity)) continue;
      seen.add(identity);
      images.push(image);
    }
  }
  return images;
};
